import cx from 'classnames';

import BreakpointsContainer from '~/components/BreakpointsContainer';
import CopyrightText from '~/components/CopyrightText';
import { useWebThemeContext } from '~/components/Layout/Context/useWebThemeContext';
import { PrivacyPolicy, TermsOfUse } from '~/components/Policies';
import { PoweredByBeehiiv } from '~/components/PoweredByBeehiiv';
import Signup from '~/components/Signup';
import SocialIcons from '~/components/SocialIcons';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';

import AccountNavGroup from '../Base/AccountNavGroup';
import GeneralNavGroup from '../Base/GeneralNavGroup';
import NavGroups from '../Base/NavGroups';
import PublicationDetails from '../Base/PublicationDetails';
import type { FooterProps } from '../types';

const Template1 = ({ publication, subscription, signupWidgetConfigs }: FooterProps) => {
  const { webTheme } = useWebThemeContext();
  const { web_template, premium_enabled, has_referral_program } = publication;
  const isLoggedIn = useIsLoggedIn();
  const isPremiumEnabled = premium_enabled;
  const isReferralEnabled = has_referral_program;

  const hasMultiplePublications = web_template?.multi_pub_ids?.length >= 1;
  const hasAuthorsPage = web_template?.author_ids?.length >= 1;
  const shouldShowAccountSection = isLoggedIn || isPremiumEnabled;
  const truncatedNavGroups = web_template.nav_groups
    ? web_template.nav_groups.filter((group) => !group.is_category_group).slice(0, shouldShowAccountSection ? 1 : 2)
    : [];

  return (
    <BreakpointsContainer
      as="footer"
      className="py-8"
      style={{
        backgroundColor: webTheme?.footer_background_color,
      }}
    >
      <div className="flex flex-col gap-y-6">
        <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-3">
          {/* Publication Details */}
          <div className="sm:px-6">
            <PublicationDetails publication={publication} />
          </div>

          {/* Nav Groups */}
          <div
            className={cx('mx-auto hidden sm:flex sm:flex-row', truncatedNavGroups.length > 0 ? 'w-10/12' : 'w-1/2')}
          >
            <GeneralNavGroup hasMultiplePublications={hasMultiplePublications} hasAuthorsPage={hasAuthorsPage} />
            {shouldShowAccountSection && (
              <AccountNavGroup
                isPremiumEnabled={isPremiumEnabled}
                isLoggedIn={isLoggedIn}
                isReferralEnabled={isReferralEnabled}
                publication={publication}
                subscription={subscription}
              />
            )}

            <NavGroups navGroups={truncatedNavGroups} />
          </div>

          {/* Signup Form & Social Icons */}
          <div className="flex flex-col items-center gap-y-4 sm:items-start sm:px-6">
            <Signup signupStyles={signupWidgetConfigs} />
            <SocialIcons
              publication={publication}
              bgColor={webTheme?.footer_text_color}
              fillColor={webTheme?.footer_background_color}
            />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-y-4 sm:flex-row sm:justify-between sm:px-6">
          <div className="w-full text-center sm:w-1/2 sm:text-left">
            <CopyrightText text={publication?.copyright_text || publication.name} />
          </div>

          <div className="flex w-full flex-col items-center gap-y-4 sm:w-1/2 sm:flex-row sm:justify-end sm:gap-x-4">
            <div className="flex gap-x-2 sm:gap-x-5">
              <PrivacyPolicy />
              <TermsOfUse />
            </div>

            <PoweredByBeehiiv publication={publication} />
          </div>
        </div>
      </div>
    </BreakpointsContainer>
  );
};

export default Template1;
